import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { OnboardingStep } from './OnboardingStep';
import { useOnboardingContext } from './OnboardingProvider';
import { WelcomeStep } from './steps/WelcomeStep';
import { FeaturesStep } from './steps/FeaturesStep';
import { GetStartedStep } from './steps/GetStartedStep';

export const OnboardingModal = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const {
    isVisible,
    currentStep,
    totalSteps,
    currentStepData,
    isFirstStep,
    isLastStep,
    canGoNext,
    canGoPrevious,
    nextStep,
    previousStep,
    close,
    complete
  } = useOnboardingContext();

  const handleNext = () => {
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const handleSkip = () => {
    close();
  };

  const handleClose = () => {
    close();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <WelcomeStep />;
      case 2:
        return <FeaturesStep />;
      case 3:
        return <GetStartedStep />;
      default:
        return <WelcomeStep />;
    }
  };

  if (!isVisible) {
    return null;
  }

  // Mobile: Pantalla completa sin overlay, fondo negro
  if (isMobile) {
    const isHighScreen = window.innerHeight > 600;
    
    return (
      <div className="fixed inset-0 w-full h-full bg-black z-[9999] flex flex-col">
        {/* Header con Atrás, paginación y Saltar - Siempre arriba */}
        <div className="flex justify-between items-center px-6 pt-8 pb-4">
          {/* Botón Atrás */}
          <div className="w-16">
            {canGoPrevious && (
              <button
                onClick={previousStep}
                className="text-xs text-white hover:text-[#FFB447] transition-colors flex items-center gap-1"
              >
                ← Atrás
              </button>
            )}
          </div>
          
          {/* Indicadores de paginación */}
          <div className="flex items-center gap-2">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <div
                key={index}
                className={`w-[9px] h-[9px] rounded-full transition-colors ${
                  index + 1 === currentStep ? 'bg-[#FFB447]' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          
          {/* Botón Saltar */}
          <div className="w-16 flex justify-end">
            <button
              onClick={handleClose}
              className="text-xs text-white hover:text-[#FFB447] transition-colors"
            >
              Saltar
            </button>
          </div>
        </div>

        {/* Título fijo */}
        <div className={`px-6 ${isHighScreen ? ' pt-8' : 'pb-8 pt-8'}`}>
          <AnimatePresence mode="wait">
            <motion.h1 
              key={currentStep}
              className="text-white !font-[500] text-[22px] leading-[26px] !font-archivo normal-case text-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ 
                duration: 0.4,
                ease: "easeInOut"
              }}
            >
              {currentStep === 1 ? 'Guardá todas tus maquetas en un mismo lugar' : 
               currentStep === 2 ? 'Asigná roles a los participantes de tu proyecto' :
               'Compartí tu música de la forma que más te guste'}
            </motion.h1>
          </AnimatePresence>
        </div>

        {/* Contenido principal - altura flexible */}
        <div className="flex-1 flex flex-col justify-center px-10">
          {renderStepContent()}
        </div>

        {/* Botón inferior fijo */}
        <div className="px-4 pb-8 pt-4">
          <button
            onClick={handleNext}
            className="w-full bg-[#FFB447] hover:bg-[#E6A041] text-black font-medium py-3 px-6 rounded-lg transition-colors"
          >
            {isLastStep ? 'Comenzar' : 'Continuar'}
          </button>
        </div>
      </div>
    );
  }

  // Desktop: Centrado con overlay, medidas específicas
  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-[9999] flex items-center justify-center p-4">
      <div 
        className="w-full max-w-[585px] h-[438px] bg-[#060606] rounded-[14px] flex flex-col"
        style={{
          border: '1.40px solid #FFB447'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <AnimatePresence mode="wait" key={currentStep}>
          <OnboardingStep
            title={currentStepData.title}
            description={currentStepData.description}
            currentStep={currentStep}
            totalSteps={totalSteps}
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            canGoNext={canGoNext}
            canGoPrevious={canGoPrevious}
            onNext={handleNext}
            onPrevious={previousStep}
            onClose={handleClose}
            onSkip={handleSkip}
          >
            <div className="w-full">
              {renderStepContent()}
            </div>
          </OnboardingStep>
        </AnimatePresence>
      </div>
    </div>
  );
};